/** leads/CertExpiryPage.tsx — 인증 만료 임박 공통 화면
 *  venture: 기존 벤처 후보 데이터의 expiring_months 필터 재사용 — 유효종료 임박순
 *  mainbiz·innobiz: cert_candidates 명단 (CSV 업로드 → 회사당 최신 만료일 대표 · 임박순)
 *  FP 배정은 리드 승격(사업자번호 확보) 연동 후 활성화 — 다음 배치 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { unassignLead, listCandidates, candidateMeta, uploadCandidateCsv, certList, certMeta, certUpload, promoteExpiryCandidates, certPromote, getMeta, renewCertStart, renewCertStop, renewCertStatus, renewCertRow, renewVentureStart, renewVentureStatus, renewVentureRow } from './api';
import type { CandidateMeta } from './types';
import LeadAssignModal from './LeadAssignModal';
import type { LeadMeta } from './types';
import type { CertRow, CertMeta } from './api';
import type { Candidate } from './types';

export type ExpiryKind = 'venture' | 'mainbiz' | 'innobiz';
interface Props { kind: ExpiryKind; onToast: (m: string, t?: string) => void }

const KIND_LABEL: Record<ExpiryKind, string> = { venture: '벤처', mainbiz: '메인비즈', innobiz: '이노비즈' };

/** 원 단위 → 억 표기 (음수 지원) */
function eok(v?: number | null): string {
  if (v == null) return '-';
  const e = v / 1e8;
  const a = Math.abs(e);
  const s = a >= 100 ? e.toFixed(0) : e.toFixed(1);
  return s + '억';
}
function FsCell({ y, sale, op }: { y?: number | null; sale?: number | null; op?: number | null }) {
  if (!y) return <span className="mut" style={{ color: '#C0C4CC' }}>-</span>;
  return (
    <span style={{ fontSize: 11.5, fontVariantNumeric: 'tabular-nums' }} title={y + '년 결산'}>
      {eok(sale)} <span style={{ color: (op ?? 0) < 0 ? '#B91C1C' : '#6B7280' }}>/ {eok(op)}</span>
      <span style={{ color: '#C0C4CC', fontSize: 10 }}> ’{String(y).slice(2)}</span>
    </span>
  );
}

/** 관리자 DM 새 창 열기 — 인증 헤더 필요해서 fetch 후 문서 기록.
 *  창은 클릭 시점(동기)에 먼저 연다 — fetch 뒤에 열면 제스처 컨텍스트 소멸로 크롬도 차단 (QA 20인 검증 A) */
async function openDm(url: string): Promise<void> {
  const w = window.open('', '_blank');
  if (!w) throw new Error('팝업이 차단되었습니다 — 브라우저 팝업 허용 후 다시 시도하세요');
  w.document.write('<p style="font-family:sans-serif;padding:30px;color:#6B7280">DM 안내장 생성 중…</p>');
  try {
    const res = await fetch(url, { headers: { Authorization: 'Bearer ' + (localStorage.getItem('token') || '') } });
    if (!res.ok) { const j = await res.json().catch(() => null); throw new Error((j && (j as { error?: string }).error) || 'DM 생성 실패'); }
    const html = await res.text();
    w.document.open(); w.document.write(html); w.document.close();
  } catch (e) { w.close(); throw e; }
}

function dday(to: string | null): number | null {
  if (!to) return null;
  const t = new Date(String(to).slice(0, 10) + 'T00:00:00');
  const n = new Date(); n.setHours(0, 0, 0, 0);
  return Math.round((t.getTime() - n.getTime()) / 86400000);
}
// RenewBadge 제거(2026-08-11): 갱신 확인 결과는 뱃지 대신 만료일 자체가 새 기간으로 갱신되어 표기됨
// (백엔드 applyRenewal이 새 만료일 행을 추가 → 회사당 최신 만료일 대표 표시)

function DBadge({ d }: { d: number | null }) {
  if (d == null) return <span className="ce-dd none">-</span>;
  const cls = d < 0 ? 'none' : d <= 30 ? 'red' : d <= 90 ? 'amber' : 'teal';
  return <span className={'ce-dd ' + cls}>{d < 0 ? 'D+' + Math.abs(d) : d === 0 ? 'D-Day' : 'D-' + d}</span>;
}

/** 벤처 만료 — 기존 벤처 후보 재사용 */
function VentureExpiryView({ onToast }: { onToast: Props['onToast'] }) {
  const [rows, setRows] = useState<Candidate[]>([]);
  const [sel, setSel] = useState<Record<string, boolean>>({});
  const [assignIds, setAssignIds] = useState<string[] | null>(null);
  const [leadMeta, setLeadMeta] = useState<LeadMeta | null>(null);
  const [promoting, setPromoting] = useState(false);
  const [renewSt, setRenewSt] = useState<{ running: boolean; total: number; checked: number; renewed: number; active: number; errors: number; pausedByQuota: boolean } | null>(null);
  const [hideRenewed, setHideRenewed] = useState(false);
  useEffect(() => { getMeta().then(setLeadMeta).catch(() => setLeadMeta(null)); }, []);
  const visibleRows = rows.filter((c) => !hideRenewed || c.renew_status !== 'valid'); // 화면 표시 대상 (전체선택 기준)
  const allChecked = visibleRows.length > 0 && visibleRows.every((c) => sel[c.id]);
  const toggleAll = (on: boolean) => setSel(() => { const n: Record<string, boolean> = {}; if (on) visibleRows.forEach((c) => { n[c.id] = true; }); return n; }); // 전체 선택/해제 (2026-08-11)
  const startRenew = async () => {
    const ids = Object.keys(sel).filter((k) => sel[k]); // 갱신여부 확인은 체크된 업체만 (2026-08-11)
    if (ids.length === 0) { onToast('갱신여부를 확인할 업체를 좌측 체크박스로 선택하세요', 'error'); return; }
    try {
      const r = await renewVentureStart(ids);
      onToast('갱신여부 확인 시작 — 선택 ' + r.total.toLocaleString() + '개사 (중소벤처24 증명서 API)');
      const poll = async () => {
        const s2 = await renewVentureStatus().catch(() => null);
        if (s2) setRenewSt(s2);
        if (s2 && s2.running) window.setTimeout(poll, 3000);
        else { load(); onToast('갱신확인 완료 — 갱신 확인 ' + (s2 ? s2.renewed.toLocaleString() : '?') + '개사 · 만료일이 새 유효기간으로 갱신됨'); }
      };
      window.setTimeout(poll, 2000);
    } catch (e) { onToast((e as Error).message, 'error'); }
  };
  const doPromote = async (ids: string[]) => {
    setPromoting(true);
    try {
      const r = await promoteExpiryCandidates(ids);
      if (r.failed.length > 0) onToast(`승격 실패 ${r.failed.length}건 — ${r.failed[0].corp_name}: ${r.failed[0].reason}`, r.leadIds.length ? undefined : 'error');
      if (r.leadIds.length > 0) setAssignIds(r.leadIds);
      setSel({});
    } catch (e) { onToast((e as Error).message, 'error'); }
    setPromoting(false);
  };
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [months, setMonths] = useState(6);
  const [q, setQ] = useState('');
  const [region, setRegion] = useState('');
  const [sigungu, setSigungu] = useState('');
  const [cmeta, setCmeta] = useState<CandidateMeta | null>(null);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const vFileRef = useRef<HTMLInputElement>(null);
  const pageSize = 30;
  useEffect(() => { candidateMeta().then(setCmeta).catch(() => setCmeta(null)); }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await listCandidates({ page, pageSize, expiring_months: months, q: q || undefined, region: region || undefined, sigungu: sigungu || undefined });
      setRows(r.rows); setTotal(r.total);
    } catch (e) { onToast((e as Error).message, 'error'); }
    setLoading(false);
  }, [page, months, q, region, sigungu, onToast]);
  useEffect(() => { load(); }, [load]);
  const onVentureFile = async (f: File | null) => {
    if (!f) return;
    setUploading(true);
    onToast('업로드 처리 중… (연 4만행, 수 초 소요)');
    try {
      const r = await uploadCandidateCsv(f);
      onToast(`벤처명단 반영 — 신규 ${r.inserted.toLocaleString()} · 중복 ${r.duplicated.toLocaleString()}` + (r.anomalies && r.anomalies.length ? ` · 충돌 ${r.anomalies.length}건은 벤처기업 탭에서 검토` : ''));
      load(); candidateMeta().then(setCmeta).catch(() => undefined);
    } catch (e) { onToast((e as Error).message, 'error'); }
    setUploading(false);
    if (vFileRef.current) vFileRef.current.value = '';
  };

  const pages = Math.max(1, Math.ceil(total / pageSize));
  return (
    <div>
      <div className="ce-chips">
        {[{ m: 1, l: 'D-30 이내' }, { m: 3, l: 'D-90 이내' }, { m: 6, l: 'D-180 이내' }, { m: 12, l: '1년 이내' }].map((c) => (
          <button key={c.m} className={'ce-chip' + (months === c.m ? ' on' : '')} onClick={() => { setMonths(c.m); setPage(1); }}>{c.l}</button>
        ))}
        <span className="ce-total">{loading ? '조회 중…' : `${total.toLocaleString()}개사 · 만료 임박순`}</span>
      </div>
      <div className="ce-bar">
        <input className="ce-inp" placeholder="업체명 검색" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') { setPage(1); load(); } }} />
        <select className="ce-sel" value={region} onChange={(e) => { setRegion(e.target.value); setSigungu(''); setPage(1); }}>
          <option value="">지역 전체</option>
          {(cmeta?.regions || []).map((r) => <option key={r} value={r}>{r}</option>)}
        </select>
        <select className="ce-sel" value={sigungu} disabled={!region} onChange={(e) => { setSigungu(e.target.value); setPage(1); }}>
          <option value="">{region ? '시군구 전체' : '시군구 (지역 먼저)'}</option>
          {(cmeta?.sigungus || []).filter((s) => s.region === region).map((s) => <option key={s.sigungu} value={s.sigungu}>{s.sigungu}</option>)}
        </select>
        {renewSt && renewSt.running
          ? <button className="ce-btn" style={{ background: '#7C3AED' }} onClick={() => renewCertStop().catch(() => undefined)}>갱신확인 중 ({renewSt.checked.toLocaleString()}/{renewSt.total.toLocaleString()} · 갱신 {renewSt.renewed.toLocaleString()})</button>
          : <button className="ce-btn" style={{ background: '#7C3AED' }} title="중소벤처24 증명서 API로 현재 유효기간 확인 — 갱신됐으면 만료일이 새 기간으로 갱신됨" onClick={startRenew}>갱신여부 확인</button>}
        <label style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 12, color: '#4B5563', cursor: 'pointer' }}>
          <input type="checkbox" checked={hideRenewed} onChange={(e) => setHideRenewed(e.target.checked)} /> 이미 갱신됨 숨기기
        </label>
        <button className="ce-btn" disabled={promoting || Object.values(sel).filter(Boolean).length === 0} onClick={() => doPromote(Object.keys(sel).filter((k) => sel[k]))}>
          {promoting ? '승격 중…' : `선택 ${Object.values(sel).filter(Boolean).length}건 배정`}
        </button>
      </div>
      <table className="ce-tbl">
        <thead><tr><th style={{ width: 28 }}><input type="checkbox" checked={allChecked} onChange={(e) => toggleAll(e.target.checked)} title="전체 선택/해제" /></th><th>업체명</th><th>대표자</th><th>사업자/법인번호</th><th>업종</th><th>주소</th><th>설립</th><th>매출/영업이익</th><th>유효기간</th><th>만료</th><th>상태</th><th>배정</th><th></th></tr></thead>
        <tbody>
          {visibleRows.map((c) => (
            <tr key={c.id}>
              <td><input type="checkbox" checked={!!sel[c.id]} onChange={() => setSel((p) => ({ ...p, [c.id]: !p[c.id] }))} /></td>
              <td className="nm">{c.corp_name}<div style={{ fontSize: 10.5, color: '#9CA3AF', fontWeight: 400 }}>{c.confirm_type || ''}</div></td>
              <td className="mut">{c.ceo_masked || '-'}</td>
              <td className="mut" style={{ fontVariantNumeric: 'tabular-nums', fontSize: 11.5 }}>
                {c.biz_no ? <span style={{ color: '#059669', fontWeight: 600 }}>{c.biz_no}</span> : (c.corp_no ? <span title="법인등록번호 — 배정 시 사업자번호 자동 확보">법인 {c.corp_no}</span> : <span style={{ color: '#B45309' }}>미확보</span>)}
              </td>
              <td className="mut" style={{ maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={c.industry || ''}>{c.industry || '-'}</td>
              <td className="mut" style={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={c.address || ''}>{c.address || (c.region + (c.sigungu ? ' ' + c.sigungu : ''))}</td>
              <td className="mut">{c.estb_dt ? String(c.estb_dt).slice(0, 4) : '-'}</td>
              <td><FsCell y={c.fs_year} sale={c.fs_sale} op={c.fs_op} /></td>
              <td className="mut">~ {String(c.venture_to || '').slice(0, 10)}</td>
              <td><DBadge d={dday(c.venture_to)} /></td>
              <td>
                {c.lead_id ? <span className="ce-st lead">리드 등록</span> : <span className="ce-st">미배정</span>}
              </td>
              <td style={{ whiteSpace: 'nowrap' }}>
                {c.lead_assigned && c.fp_name ? (<>
                  <b style={{ fontSize: 12.5 }}>{c.fp_name}</b>
                  <button className="ce-btn sub" style={{ padding: '3px 9px', fontSize: 10.5, marginLeft: 6 }} title="한 번 클릭으로 회수 — 미배정 복귀"
                    onClick={() => unassignLead(c.lead_id as string).then(() => { onToast((c.fp_name || '') + ' 배정 회수 완료'); load(); }).catch((e) => onToast((e as Error).message, 'error'))}>회수</button>
                </>) : <span className="mut">-</span>}
              </td>
              <td style={{ whiteSpace: 'nowrap' }}>
                <button className="ce-btn sub" style={{ padding: '4px 8px', fontSize: 11, marginRight: 4 }} title="중소벤처24 API로 현재 갱신여부 확인"
                  onClick={() => renewVentureRow(c.id).then((r) => { onToast(r.status === 'valid' ? '갱신 확인 — 새 유효기간 ' + (r.startDate ? r.startDate + ' ~ ' : '') + (r.endDate || '') + ' 반영' : r.status === 'none' ? '현재 유효 인증 없음 — 영업 대상' : '확인 실패 (발급기관 연계 오류 — 잠시 후 재시도)'); load(); }).catch((e) => onToast((e as Error).message, 'error'))}>갱신확인</button>
                <button className="ce-btn sub" style={{ padding: '4px 10px', fontSize: 11, marginRight: 4 }} title="만료 안내 DM 인쇄"
                  onClick={() => openDm('/api/lead-candidates/' + c.id + '/dm').catch((e) => onToast((e as Error).message, 'error'))}>DM</button>
                <button className="ce-btn" style={{ padding: '4px 10px', fontSize: 11 }} disabled={promoting || (!c.corp_no && !c.biz_no && !c.lead_id)}
                title={!c.corp_no && !c.biz_no && !c.lead_id ? '법인번호 미확보 — 벤처기업 탭에서 [법인번호+증자이력] 먼저' : ''}
                onClick={() => doPromote([c.id])}>배정</button></td>
            </tr>
          ))}
          {rows.length === 0 && !loading && <tr><td colSpan={13} className="ce-none">해당 기간에 만료되는 벤처기업이 없습니다</td></tr>}
        </tbody>
      </table>
      <div className="ce-pg" style={{ justifyContent: pages > 1 ? 'center' : 'flex-start', position: 'relative' }}>
        <input ref={vFileRef} type="file" accept=".csv" style={{ display: 'none' }} onChange={(e) => onVentureFile(e.target.files ? e.target.files[0] : null)} />
        <button disabled={uploading} onClick={() => vFileRef.current?.click()} title="벤처기업명단 CSV 업로드 (연도별 파일 — 추가만, 충돌은 벤처기업 탭에서 검토)"
          style={{ position: 'absolute', left: 0, background: 'none', border: 'none', cursor: 'pointer', fontSize: 11, color: '#C0C4CC', textDecoration: 'underline dotted', opacity: uploading ? 1 : 0.7 }}>
          {uploading ? '업로드 중…' : '최신 명단 CSV 업로드'}</button>
        {pages > 1 && (<>
          <button disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>이전</button>
          <span>{page} / {pages}</span>
          <button disabled={page >= pages} onClick={() => setPage((p) => p + 1)}>다음</button>
        </>)}
      </div>
      {assignIds && <LeadAssignModal leadIds={assignIds} meta={leadMeta}
        onClose={() => setAssignIds(null)}
        onDone={() => { setAssignIds(null); load(); onToast('FP 배정 완료 — 리드포털에 만료 D-day와 함께 표시됩니다'); }}
        onToast={onToast} />}
    </div>
  );
}

/** 메인비즈·이노비즈 — cert_candidates 명단 */
function CertDbView({ kind, onToast }: { kind: 'mainbiz' | 'innobiz'; onToast: Props['onToast'] }) {
  const [rows, setRows] = useState<CertRow[]>([]);
  const [meta, setMeta] = useState<CertMeta | null>(null);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [months, setMonths] = useState(6);
  const [q, setQ] = useState('');
  const [region, setRegion] = useState('');
  const [sigungu, setSigungu] = useState('');
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [sel, setSel] = useState<Record<string, boolean>>({});
  const [assignIds, setAssignIds] = useState<string[] | null>(null);
  const [leadMeta, setLeadMeta] = useState<LeadMeta | null>(null);
  const [promoting, setPromoting] = useState(false);
  useEffect(() => { getMeta().then(setLeadMeta).catch(() => setLeadMeta(null)); }, []);
  const doPromote = async (ids: string[]) => {
    setPromoting(true);
    try {
      const r = await certPromote(kind, ids);
      if (r.failed.length > 0) onToast(`승격 실패 ${r.failed.length}건 — ${r.failed[0].corp_name}: ${r.failed[0].reason}`, r.leadIds.length ? undefined : 'error');
      if (r.leadIds.length > 0) setAssignIds(r.leadIds);
      setSel({});
    } catch (e) { onToast((e as Error).message, 'error'); }
    setPromoting(false);
  };
  const [renewSt, setRenewSt] = useState<{ running: boolean; kind?: string; total: number; checked: number; renewed: number; active: number; errors: number; pausedByQuota: boolean } | null>(null);
  const [hideRenewed, setHideRenewed] = useState(false);
  const [onlyAssigned, setOnlyAssigned] = useState(false); // 배정만 (2026-07-18)
  const fileRef = useRef<HTMLInputElement>(null);
  const pageSize = 30;
  const startRenew = async () => {
    const ids = Object.keys(sel).filter((k) => sel[k]); // 갱신여부 확인은 체크된 업체만 (2026-08-11)
    if (ids.length === 0) { onToast('갱신여부를 확인할 업체를 좌측 체크박스로 선택하세요', 'error'); return; }
    try {
      const r = await renewCertStart(kind, ids);
      onToast('갱신여부 확인 시작 — 선택 ' + r.total.toLocaleString() + '개사 (중소벤처24 증명서 API)');
      const poll = async () => {
        const s2 = await renewCertStatus().catch(() => null);
        if (s2) setRenewSt(s2);
        if (s2 && s2.running) window.setTimeout(poll, 3000);
        else { load(); onToast('갱신확인 완료 — 갱신 확인 ' + (s2 ? s2.renewed.toLocaleString() : '?') + '개사 · 만료일이 새 유효기간으로 갱신됨'); }
      };
      window.setTimeout(poll, 2000);
    } catch (e) { onToast((e as Error).message, 'error'); }
  };

  const loadMeta = useCallback(() => { certMeta(kind).then(setMeta).catch(() => setMeta(null)); }, [kind]);
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await certList({ kind, page, pageSize, months, q: q || undefined, region: region || undefined, sigungu: sigungu || undefined, assigned: onlyAssigned ? 'yes' : undefined });
      setRows(r.rows); setTotal(r.total);
    } catch (e) { onToast((e as Error).message, 'error'); }
    setLoading(false);
  }, [kind, page, months, q, region, sigungu, onlyAssigned, onToast]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadMeta(); }, [loadMeta]);

  const onFile = async (f: File | null) => {
    if (!f) return;
    setUploading(true);
    try {
      const r = await certUpload(kind, f);
      onToast(`업로드 완료 — 파싱 ${r.parsed.toLocaleString()}행 · 신규 ${r.inserted.toLocaleString()} · 갱신 ${(r.updated ?? 0).toLocaleString()} · 중복 ${r.skipped.toLocaleString()}` + (r.errors.length ? ` · 오류 ${r.errors.length}행` : ''));
      setPage(1); load(); loadMeta();
    } catch (e) { onToast((e as Error).message, 'error'); }
    setUploading(false);
    if (fileRef.current) fileRef.current.value = '';
  };

  const pages = Math.max(1, Math.ceil(total / pageSize));
  const empty = !loading && total === 0 && !q && !region;
  const visibleRows = rows.filter((c) => !hideRenewed || c.renew_status !== 'valid'); // 화면 표시 대상
  const selectableRows = visibleRows.filter((c) => c.biz_no || c.lead_id); // 선택 가능(사업자번호/리드 보유)
  const allChecked = selectableRows.length > 0 && selectableRows.every((c) => sel[c.id]);
  const toggleAll = (on: boolean) => setSel(() => { const n: Record<string, boolean> = {}; if (on) selectableRows.forEach((c) => { n[c.id] = true; }); return n; }); // 전체 선택/해제 (2026-08-11)
  return (
    <div>
      <input ref={fileRef} type="file" accept=".csv" style={{ display: 'none' }} onChange={(e) => onFile(e.target.files ? e.target.files[0] : null)} />
      <div className="ce-chips">
        {[{ m: 1, l: 'D-30 이내', c: meta?.d30 }, { m: 3, l: 'D-90 이내', c: meta?.d90 }, { m: 6, l: 'D-180 이내', c: meta?.d180 }, { m: 0, l: '전체', c: meta?.total }].map((c) => (
          <button key={c.l} className={'ce-chip' + (months === c.m ? ' on' : '')} onClick={() => { setMonths(c.m); setPage(1); }}>
            {c.l}{c.c !== undefined ? ' ' + Number(c.c).toLocaleString() : ''}
          </button>
        ))}
        <span className="ce-total">{loading ? '조회 중…' : `${total.toLocaleString()}개사 · 만료 임박순`}</span>
      </div>
      <div className="ce-bar">
        <input className="ce-inp" placeholder="업체명 검색" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') { setPage(1); load(); } }} />
        <select className="ce-sel" value={region} onChange={(e) => { setRegion(e.target.value); setSigungu(''); setPage(1); }}>
          <option value="">지역 전체</option>
          {(meta?.regions || []).map((r) => <option key={r} value={r}>{r}</option>)}
        </select>
        <select className="ce-sel" value={sigungu} disabled={!region} title={(meta?.sigungus || []).length === 0 ? '시군구는 명단 업로드(지역 컬럼) 반영 후 채워집니다' : ''} onChange={(e) => { setSigungu(e.target.value); setPage(1); }}>
          <option value="">{region ? '시군구 전체' : '시군구 (지역 먼저)'}</option>
          {(meta?.sigungus || []).filter((s) => s.region === region).map((s) => <option key={s.sigungu} value={s.sigungu}>{s.sigungu}</option>)}
        </select>
        {/* 사업자번호 자동확보 버튼 제거(2026-08-11): 명단(공식배포본)에 사업자번호 포함 → 자동확보 불필요 */}
        {renewSt && renewSt.running && renewSt.kind === kind
          ? <button className="ce-btn" style={{ background: '#7C3AED' }} onClick={() => renewCertStop().catch(() => undefined)}>갱신확인 중 ({renewSt.checked.toLocaleString()}/{renewSt.total.toLocaleString()} · 갱신 {renewSt.renewed.toLocaleString()})</button>
          : <button className="ce-btn" style={{ background: '#7C3AED' }} disabled={!!(renewSt && renewSt.running)} title="중소벤처24 증명서 API로 현재 유효기간 확인 — 갱신됐으면 만료일이 새 기간으로 갱신됨" onClick={startRenew}>갱신여부 확인</button>}
        <label style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 12, color: '#4B5563', cursor: 'pointer' }}>
          <input type="checkbox" checked={hideRenewed} onChange={(e) => setHideRenewed(e.target.checked)} /> 이미 갱신됨 숨기기
        </label>
        <label style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 12, color: '#4B5563', cursor: 'pointer' }}>
          <input type="checkbox" checked={onlyAssigned} onChange={(e) => { setOnlyAssigned(e.target.checked); setPage(1); }} /> 배정만
        </label>
        <button className="ce-btn" disabled={promoting || Object.values(sel).filter(Boolean).length === 0} onClick={() => doPromote(Object.keys(sel).filter((k) => sel[k]))}>
          {promoting ? '승격 중…' : `선택 ${Object.values(sel).filter(Boolean).length}건 배정`}
        </button>
      </div>
      {/* 사업자번호 확보/동명검토/미확보 요약 제거(2026-08-11): 명단에 사업자번호 포함 → 자동확보 통계 불필요 */}
      {empty ? (
        <div className="ce-empty">
          <div className="big">📥</div>
          <div className="t1">{KIND_LABEL[kind]} 명단을 업로드하세요</div>
          <div className="t2">
            공공데이터 혁신형중소기업 현황 CSV (cp949 자동 인식 · 헤더 별칭 흡수)<br />
            연도별 파일을 계속 올리면 갱신 이력이 누적되고, 화면은 회사당 최신 만료일로 표시됩니다.
          </div>
          <button className="ce-btn" disabled={uploading} onClick={() => fileRef.current?.click()}>{uploading ? '적재 중…' : '명단 CSV 업로드'}</button>
        </div>
      ) : (
        <>
          <table className="ce-tbl">
            <thead><tr><th style={{ width: 28 }}><input type="checkbox" checked={allChecked} onChange={(e) => toggleAll(e.target.checked)} title="전체 선택/해제" /></th><th>업체명</th><th>대표자</th><th>사업자번호</th><th>업종</th><th>지역</th><th>설립</th><th>매출/영업이익</th><th>만료일</th><th>만료</th><th>상태</th><th>배정</th><th></th></tr></thead>
            <tbody>
              {visibleRows.map((c) => (
                <tr key={c.id}>
                  <td><input type="checkbox" checked={!!sel[c.id]} disabled={!c.biz_no && !c.lead_id} onChange={() => setSel((p) => ({ ...p, [c.id]: !p[c.id] }))} title={!c.biz_no && !c.lead_id ? '사업자번호 미확보' : ''} /></td>
                  <td className="nm">{c.corp_name}</td>
                  <td className="mut">{c.ceo_name || '-'}</td>
                  <td className="mut" style={{ fontVariantNumeric: 'tabular-nums', fontSize: 11.5 }}>{c.biz_no ? <span style={{ color: '#059669', fontWeight: 600 }}>{c.biz_no}</span> : <span style={{ color: '#B45309' }}>미확보</span>}</td>
                  <td className="mut">{c.industry || '-'}</td>
                  <td className="mut" title={c.address || ''}>{c.region || '-'}{c.sigungu ? ' ' + c.sigungu : ''}</td>
                  <td className="mut">{c.estb_dt ? String(c.estb_dt).slice(0, 4) : '-'}</td>
                  <td><FsCell y={c.fs_year} sale={c.fs_sale} op={c.fs_op} /></td>
                  <td className="mut">~ {String(c.expire_date).slice(0, 10)}</td>
                  <td><DBadge d={dday(c.expire_date)} /></td>
                  <td>
                    {c.lead_id ? <span className="ce-st lead">리드 등록</span> : <span className="ce-st">미배정</span>}
                  </td>
                  <td style={{ whiteSpace: 'nowrap' }}>
                    {c.lead_assigned && c.fp_name ? (<>
                      <b style={{ fontSize: 12.5 }}>{c.fp_name}</b>
                      <button className="ce-btn sub" style={{ padding: '3px 9px', fontSize: 10.5, marginLeft: 6 }} title="한 번 클릭으로 회수 — 미배정 복귀"
                        onClick={() => unassignLead(c.lead_id as string).then(() => { onToast((c.fp_name || '') + ' 배정 회수 완료'); load(); }).catch((e) => onToast((e as Error).message, 'error'))}>회수</button>
                    </>) : <span className="mut">-</span>}
                  </td>
                  <td style={{ whiteSpace: 'nowrap' }}>
                    <button className="ce-btn sub" style={{ padding: '4px 8px', fontSize: 11, marginRight: 4 }} title="중소벤처24 API로 현재 갱신여부 확인"
                      onClick={() => renewCertRow(kind, c.id).then((r) => { onToast(r.status === 'valid' ? '갱신 확인 — 새 유효기간 ' + (r.startDate ? r.startDate + ' ~ ' : '') + (r.endDate || '') + ' 반영' : r.status === 'none' ? '현재 유효 인증 없음 — 영업 대상' : '확인 실패 (발급기관 연계 오류 — 잠시 후 재시도)'); load(); }).catch((e) => onToast((e as Error).message, 'error'))}>갱신확인</button>
                    <button className="ce-btn sub" style={{ padding: '4px 10px', fontSize: 11, marginRight: 4 }} title="만료 안내 DM 인쇄"
                      onClick={() => openDm('/api/cert-candidates/' + kind + '/rows/' + c.id + '/dm').catch((e) => onToast((e as Error).message, 'error'))}>DM</button>
                    <button className="ce-btn" style={{ padding: '4px 10px', fontSize: 11 }} disabled={promoting || (!c.biz_no && !c.lead_id)}
                    title={!c.biz_no && !c.lead_id ? '사업자번호 미확보' : ''}
                    onClick={() => doPromote([c.id])}>배정</button></td>
                </tr>
              ))}
              {rows.length === 0 && !loading && <tr><td colSpan={13} className="ce-none">조건에 맞는 업체가 없습니다</td></tr>}
            </tbody>
          </table>
          <div className="ce-pg" style={{ justifyContent: pages > 1 ? 'center' : 'flex-start', position: 'relative' }}>
            <button disabled={uploading} onClick={() => fileRef.current?.click()} title="최신 명단 CSV 업로드 (연도별 파일 누적 — 회사당 최신 만료일 대표)"
              style={{ position: 'absolute', left: 0, background: 'none', border: 'none', cursor: 'pointer', fontSize: 11, color: '#C0C4CC', textDecoration: 'underline dotted', opacity: uploading ? 1 : 0.7 }}>
              {uploading ? '적재 중…' : '최신 명단 CSV 업로드'}</button>
            {pages > 1 && (<>
              <button disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>이전</button>
              <span>{page} / {pages}</span>
              <button disabled={page >= pages} onClick={() => setPage((p) => p + 1)}>다음</button>
            </>)}
          </div>
        </>
      )}
      {assignIds && <LeadAssignModal leadIds={assignIds} meta={leadMeta}
        onClose={() => setAssignIds(null)}
        onDone={() => { setAssignIds(null); load(); onToast('FP 배정 완료 — 리드포털에 만료 D-day와 함께 표시됩니다'); }}
        onToast={onToast} />}
    </div>
  );
}

export default function CertExpiryPage({ kind, onToast }: Props) {
  if (kind === 'venture') return <VentureExpiryView onToast={onToast} />;
  return <CertDbView kind={kind} onToast={onToast} />;
}
